// Artwork Editor — parameterized editor route.
// -----------------------------------------------------------------
// Per docs/architecture/dispatches/m6-0-read-only-page-shell.md
// § 4.3 (`artwork-editor_.$arkId.tsx` contract):
//   "Parameterized route via TanStack Router file convention
//    (mirrors `tour-planner_.$planId.tsx`). Reads `arkId` from
//    params + `mode` from search params (default `'staging'`).
//    Gated by `VITE_FF_ARTWORK_EDITOR`; flag-off redirects to
//    `/hello`. Mounts `<ArtworkEditorShell ark_id={arkId}
//    mode={mode} />`."
//
// TanStack Router trailing-underscore convention: the `_` in the
// filename `artwork-editor_.$arkId.tsx` opts out of nesting under
// `/artwork-editor`'s `<Outlet />`, mirroring the M4.1 fix at
// `tour-planner_.$planId.tsx` per M4-F11 (M4.1-P1 lead-direct
// verification 2026-05-08). Without the underscore, the parent
// route would need to render an `<Outlet />` slot, and our parent
// route renders a queue-index placeholder body (no Outlet).
//
// Search-param parsing (per § 4.3): `mode` is read from the URL
// search string. `validateSearch` is the canonical TanStack pattern
// but no other SPA route uses it today (greenfield in M6.0). We
// keep the parser local + minimal — `'production'` triggers
// production mode; everything else falls back to staging. Bookmark
// shape: `/artwork-editor/<arkId>?mode=production`.
//
// M6.0-F1 (filed at sub-agent execution): dispatch § 0.3.i row
// "Mode switching" reads "Sub-agent implements URL-prefix variant
// (mirrors Flask)" while § 4.3 says "Reads mode from search params".
// The concrete deliverable list at § 4.1 only includes this single
// route file; supporting `/artwork-editor/production/<arkId>` would
// require a second route file not in § 4.1. M6.0-1b implements the
// search-param contract per § 4.3 (the more specific spec) and
// flags the URL-prefix question for lead at M6.0-L1 — if operator
// bookmarks demand URL-prefix shape, adding
// `artwork-editor_.production.$arkId.tsx` is a 1-file follow-up.

import { Navigate, createFileRoute } from '@tanstack/react-router';

import {
  ArtworkEditorShell,
  type EditorMode,
} from '@/components/artwork-editor/ArtworkEditorShell';
import { isFeatureEnabled } from '@/lib/featureFlags';

/** Parse a search-string `mode` value. Anything other than the
 *  literal `'production'` returns the `'staging'` default. */
const parseMode = (raw: unknown): EditorMode =>
  raw === 'production' ? 'production' : 'staging';

const trimString = (raw: unknown): string | undefined =>
  typeof raw === 'string' && raw.trim() ? raw.trim() : undefined;

type ArtworkEditorSearch = {
  mode?: string;
  tour_id?: string;
  staging_filter?: string;
};

const ArtworkEditorParamRoute = () => {
  const { arkId } = Route.useParams();
  const search = Route.useSearch();
  if (!isFeatureEnabled('ARTWORK_EDITOR')) {
    return <Navigate to='/hello' replace />;
  }
  const mode = parseMode(search.mode);
  const tourId = trimString(search.tour_id);
  const stagingFilter = trimString(search.staging_filter);
  return (
    <ArtworkEditorShell
      arkId={arkId}
      mode={mode}
      {...(tourId !== undefined ? { tourId } : {})}
      {...(stagingFilter !== undefined ? { stagingFilter } : {})}
    />
  );
};

export const Route = createFileRoute(
  '/_authenticated/artwork-editor_/$arkId',
)({
  component: ArtworkEditorParamRoute,
  validateSearch: (search: Record<string, unknown>): ArtworkEditorSearch => {
    const result: ArtworkEditorSearch = {};
    if (typeof search.mode === 'string') result.mode = search.mode;
    if (typeof search.tour_id === 'string') result.tour_id = search.tour_id;
    if (typeof search.staging_filter === 'string') {
      result.staging_filter = search.staging_filter;
    }
    return result;
  },
});
